import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { FormSummary, FormSummaryItem, List } from '@feathr/components';
import { formatImportCount, hasRunFinished } from '@feathr/extender/components/ImportRunLines';

import { getRejectionLines } from './ImportRunDetails.utils';

/** The panel summarizes; the error file lists every reason. */
const MAX_REASONS = 3;

interface IRejectionBreakdownProps {
  className?: string;
  importer: Importer;
}

/**
 * The reasons the run refused the most rows, as a summary of its own with the label above the list:
 * one list entry per reason, with the server's sentence for each.
 */
function RejectionBreakdown({
  className,
  importer,
}: Readonly<IRejectionBreakdownProps>): JSX.Element | null {
  const { t } = useTranslation();

  const lines = hasRunFinished(importer) ? getRejectionLines(importer).slice(0, MAX_REASONS) : [];
  if (!lines.length) {
    return null;
  }

  // Placeholder copy, pending Product and Design.
  return (
    <FormSummary className={className} layout={'vertical'}>
      <FormSummaryItem
        label={t('Problems')}
        value={
          <List>
            {lines.map(({ code, count, message }) => {
              const formattedCount = formatImportCount(count);
              return (
                <List.Item key={code}>
                  {message
                    ? t('{{formattedCount}} rows: {{- message}}', {
                        count,
                        formattedCount,
                        message,
                      })
                    : t('{{formattedCount}} rows were not imported.', { count, formattedCount })}
                </List.Item>
              );
            })}
          </List>
        }
      />
    </FormSummary>
  );
}

export default observer(RejectionBreakdown);
